import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabaseClient";

const INACTIVITY_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours
const WARNING_BEFORE_MS = 5 * 60 * 1000;           // warn 5 min before logout
const LAST_ACTIVITY_KEY = "iscan.lastActivity";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  sessionWarning: boolean;
  secondsUntilLogout: number;
  resetActivity: () => void;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
  sessionWarning: false,
  secondsUntilLogout: 0,
  resetActivity: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [secondsUntilLogout, setSecondsUntilLogout] = useState(0);
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  const resetActivity = useCallback(() => {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    setSessionWarning(false);
  }, []);

  const signOut = useCallback(async () => {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    await supabase.auth.signOut();
  }, []);

  // Inactivity timer — only active while logged in
  useEffect(() => {
    if (!session) return;

    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    }

    const activityEvents = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"] as const;

    function onActivity() {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      setSessionWarning(false);
    }

    activityEvents.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const interval = setInterval(() => {
      if (!sessionRef.current) return;
      const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) ?? "0", 10) || Date.now();
      const elapsed = Date.now() - last;
      const remaining = INACTIVITY_TIMEOUT_MS - elapsed;

      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        supabase.auth.signOut();
      } else if (remaining <= WARNING_BEFORE_MS) {
        setSessionWarning(true);
        setSecondsUntilLogout(Math.max(0, Math.ceil(remaining / 1000)));
      } else {
        setSessionWarning(false);
      }
    }, 1000);

    return () => {
      activityEvents.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(interval);
    };
  }, [session?.access_token]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) localStorage.removeItem(LAST_ACTIVITY_KEY);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, signOut, sessionWarning, secondsUntilLogout, resetActivity }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
